import { FC, useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Image from '@base/Image';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import {
  DST,
  DST_STATES
} from '@cont/SeeAll/TestSeriesListing/DailyScholarshipTest/utils';
import { timeInMinsSecs } from '@utils/dates';
import usePreciseTimer from '@hooks/usePreciseTimer';
import { useSWRConfig } from 'swr';
import { getDSTEndpoint } from '@cont/SeeAll/TestSeriesListing/DailyScholarshipTest/apiHelper';
import useDstClickHandlers from '@cont/SeeAll/TestSeriesListing/DailyScholarshipTest/useDstClickHandlers';
import useWindowSize from '@hooks/useWindowSize';

const Container = styled(Paper)`
  display: flex;
  align-items: center;
  padding: 0;
  padding-left: 0;
  overflow: hidden;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;
const BaseMobContainer = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: block;
  }
`;
const MobContainer = styled(Paper)`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: flex;
    align-items: center;
    overflow: hidden;
    padding: ${Spacings.SPACING_4B};
  }
`;
const ContentDiv = styled.div`
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_20B} ${Spacings.SPACING_8B}${Spacings.SPACING_6B};
  flex: 1;
  ${mediaQuery.uptoMobile} {
    width: 60%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_4B}
      ${Spacings.SPACING_4B}${Spacings.SPACING_4B};
  }
`;

const Header = styled.div`
  font-size: ${Fonts.H4};
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_6B};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const SubHeader = styled.div`
  font-size: ${Fonts.H5};
  line-height: 22px;
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_2B};
`;

const StyledBtn = styled(Button)`
  height: 46px;
  margin-right: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
  }
`;
const SummaryBtn = styled(Button)`
  height: 46px;
  margin-right: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
    background: transparent;
    color: var(--color-i-all-black);
    width: 100%;
    border-top: 1px solid var(--color-i-divider);
    border-radius: 0%;
  }
`;

const TimeRemaining = styled.div`
  font-size: ${Fonts.H5};
  line-height: 22px;
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-i-red);
  margin-bottom: ${Spacings.SPACING_2B};
`;

interface DSTProps {
  data: DST;
  goalUID: string;
  // eslint-disable-next-line react/require-default-props
  goalSlug?: string;
  goalName: string;
  lps: string;
}

type TimeLeftProps = Pick<DSTProps, 'data' | 'goalUID'>;

const TimeLeft = ({ data, goalUID }: TimeLeftProps) => {
  const [time, setTime] = useState(
    Math.round(
      (new Date(data.data[0].finishTime).getTime() - new Date().getTime()) /
        1000
    )
  );
  const handler = useCallback(() => {
    setTime((data) => data - 1);
  }, []);

  const { mutate } = useSWRConfig();

  useEffect(() => {
    if (time === 0) mutate(getDSTEndpoint(goalUID));
  }, [time, mutate, goalUID]);

  usePreciseTimer(handler, 1000, true);

  if (time < 1) return null;

  return <TimeRemaining>{timeInMinsSecs(time)} left</TimeRemaining>;
};

const DSTDesktop = ({ data, goalUID, goalSlug, goalName, lps }: DSTProps) => {
  const { isMobile } = useWindowSize();
  const getDSTContent = (state) => {
    switch (state) {
      case DST_STATES.RESUME:
        return {
          imgSrc: isMobile ? '/dst/attempteddst.svg' : '/dst/attempted.svg',
          cta: {
            label: 'Resume test',
            variant: 'filled',
            color: 'black'
          }
        };

      case DST_STATES.ATTEMPTED:
        return {
          imgSrc: isMobile ? '/dst/attempteddst.svg' : '/dst/attempted.svg',
          cta: {
            label: 'Attempt summary',
            variant: 'hollow',
            color: 'black'
          }
        };

      default:
        return {
          imgSrc: isMobile ? '/dst/unattempteddst.svg' : '/dst/unattempted.svg',
          cta: {
            label: 'Take test',
            variant: 'filled',
            color: 'black'
          }
        };
    }
  };
  const { state, quizUid, sessionUid } = data.data[0];
  const { header, subHeader } = data.extraBlockInfo;
  const { imgSrc, cta } = getDSTContent(state);
  const { takeTest, viewAttempts } = useDstClickHandlers({
    goalUID,
    goalSlug,
    goalName,
    lps
  });
  const onClick = useCallback(() => {
    switch (state) {
      case DST_STATES.RESUME:
        return takeTest();

      case DST_STATES.ATTEMPTED:
        return viewAttempts(quizUid, sessionUid);

      default:
    }
  }, [state, takeTest, viewAttempts, quizUid, sessionUid]);
  const attemptedState = DST_STATES.ATTEMPTED === state;
  return (
    <>
      <Container>
        {imgSrc && (
          <Image
            src={imgSrc}
            height={118}
            width={116}
            alt="daily-scholarship-test"
          />
        )}
        <ContentDiv>
          {state === DST_STATES.RESUME && (
            <TimeLeft data={data} goalUID={goalUID} />
          )}
          <Header>{header}</Header>
          <SubHeader>{subHeader}</SubHeader>
        </ContentDiv>
        <StyledBtn
          // @ts-ignore
          label={cta.label}
          variant={cta.variant}
          color={cta.color}
          onClick={onClick}
        />
      </Container>
      <BaseMobContainer>
        <MobContainer>
          <ContentDiv>
            {state === DST_STATES.RESUME && (
              <TimeLeft data={data} goalUID={goalUID} />
            )}
            <Header>{header}</Header>
            <SubHeader>{subHeader}</SubHeader>
            {!attemptedState ? (
              <StyledBtn
                // @ts-ignore
                label={cta.label}
                variant={cta.variant}
                color={cta.color}
                onClick={onClick}
              />
            ) : (
              <></>
            )}
          </ContentDiv>
          <div style={{ width: '40%' }}>
            {imgSrc && (
              <Image
                src={imgSrc}
                height="200px"
                width="200px"
                alt="daily-scholarship-test"
              />
            )}
          </div>
        </MobContainer>
        {attemptedState ? (
          <SummaryBtn
            // @ts-ignore
            label={cta.label}
            variant={cta.variant}
            color={cta.color}
            onClick={onClick}
          />
        ) : (
          <></>
        )}
      </BaseMobContainer>
    </>
  );
};

export default DSTDesktop;
